import { useMemo, useState } from 'react';

import type {
  Entry,
  Mitfahrer,
  Person,
  RefuelEntry,
  Settings,
  TripEntry,
} from '@baba/engine/types';
import { besetzungAusListe, computeAll } from '@baba/engine';
import type { FotoFeld } from '@baba/engine/types';
import { ART_TITEL, heuteIso, NAME, zuText, zuZahl } from '../format.ts';
import { api } from '../api.ts';

/**
 * Wie das Formular auftritt.
 *
 * 'start' und 'stopp' sind die beiden Hälften eines Ausflugs, der unterwegs
 * erfasst wird (R-23). 'voll' bleibt der Weg fürs Nachtragen einer Fahrt, die
 * schon vorbei ist - ohne den liesse sich eine vergessene Fahrt von letzter
 * Woche nicht mehr eintragen.
 */
export type FormModus = 'voll' | 'start' | 'stopp';

interface Props {
  art: Entry['type'];
  modus?: FormModus;
  vorhanden?: Entry;
  entries: Entry[];
  settings: Settings;
  ich: Person;
  aufSpeichern: (
    entry: Partial<Entry>,
    fotos: { datei: File; feld: FotoFeld }[],
  ) => Promise<void>;
  aufAbbrechen: () => void;
}

type Formular = Record<string, string | boolean | undefined>;

function startwerte(
  art: Entry['type'],
  vorhanden: Entry | undefined,
  settings: Settings,
  ich: Person,
): Formular {
  if (!vorhanden) {
    const basis: Formular = { datum: heuteIso(), beschreibung: '' };
    switch (art) {
      case 'trip':
        return {
          ...basis,
          werUnterwegs: ich,
          kassierer: ich,
          zusatzkostenProKm: zuText(settings.zusatzkostenProKmDefault, 2),
          anteilEinzelstrecke: zuText(
            100 * settings.anteilEinzelstreckeDefault,
            0,
          ),
          parkgebuehrVon: 'michi',
        };
      case 'refuel':
        return { ...basis, bezahltVon: ich, volltankung: true };
      case 'repair':
      case 'fixed_cost':
        return { ...basis, bezahltVon: ich };
      case 'settlement':
        return {
          ...basis,
          richtung: ich === 'michi' ? 'michi_an_otto' : 'otto_an_michi',
        };
    }
  }

  const e = vorhanden;
  const basis: Formular = {
    datum: e.datum,
    beschreibung: e.beschreibung ?? '',
  };
  switch (e.type) {
    case 'trip':
      return {
        ...basis,
        werUnterwegs: e.werUnterwegs,
        kassierer: e.kassierer ?? 'michi',
        kmStart: zuText(e.kmStart),
        kmEnde: zuText(e.kmEnde),
        kmGefahren: zuText(e.kmGefahren),
        namenMitfahrer: e.namenMitfahrer ?? '',
        zusatzkostenProKm: zuText(e.zusatzkostenProKm, 2),
        anteilEinzelstrecke: zuText(100 * e.anteilEinzelstrecke, 0),
        parkgebuehr: e.parkgebuehr != null ? zuText(e.parkgebuehr, 2) : '',
        parkgebuehrVon: e.parkgebuehrVon ?? 'michi',
        // undefined heisst "unberührt" und lässt die Vorbelegung greifen; nur
        // ein bereits festgeschriebener Wert kommt als Text ins Feld.
        verbrauchL100kmManuell:
          e.verbrauchL100kmManuell != null
            ? zuText(e.verbrauchL100kmManuell, 2)
            : undefined,
        preisProLiterManuell:
          e.preisProLiterManuell != null
            ? zuText(e.preisProLiterManuell, 2)
            : undefined,
      };
    case 'refuel':
      return {
        ...basis,
        bezahltVon: e.bezahltVon,
        betrag: zuText(e.betrag, 2),
        liter: zuText(e.liter, 2),
        kmStand: zuText(e.kmStand),
        volltankung: e.volltankung,
      };
    case 'repair':
      return {
        ...basis,
        bezahltVon: e.bezahltVon,
        betrag: zuText(e.betrag, 2),
        kmStand: zuText(e.kmStand),
      };
    case 'settlement':
      return { ...basis, richtung: e.richtung, betrag: zuText(e.betrag, 2) };
    case 'fixed_cost':
      return {
        ...basis,
        bezahltVon: e.bezahltVon,
        betrag: zuText(e.betrag, 2),
        kategorie: e.kategorie ?? '',
      };
  }
}

/** Kennzeichnet ein Pflichtfeld. */
function Pflicht() {
  return (
    <span className="pflicht" aria-label="Pflichtfeld">
      *
    </span>
  );
}

export function EntryForm({
  art,
  modus = 'voll',
  vorhanden,
  entries,
  settings,
  ich,
  aufSpeichern,
  aufAbbrechen,
}: Props) {
  const [f, setF] = useState<Formular>(() =>
    startwerte(art, vorhanden, settings, ich),
  );
  /*
    Die Mitfahrerliste (R-24) passt nicht in das flache Formularobjekt und
    lebt deshalb daneben. Sie geht in keine Berechnung ein - sie belegt nur
    die Anzahl vor und merkt sich, wer schon bezahlt hat.
  */
  const [mitfahrer, setMitfahrer] = useState<Mitfahrer[]>(
    () =>
      (vorhanden?.type === 'trip' ? vorhanden.mitfahrer : undefined)?.map(
        (m) => ({ ...m }),
      ) ?? [],
  );
  /*
    Fotos tragen mit, wozu sie gehören: 'km_start' und 'km_ende' sind die
    beiden Tachobilder eines Ausflugs (R-27), 'beleg' alles andere. Ohne das
    Feld liessen sich später zwei Tachofotos nicht auseinanderhalten.

    Die Schreibweise stammt aus der Datenbank: photo.feld lässt nur
    'km_start', 'km_ende', 'km_stand' und 'beleg' zu. In der Zwischenform
    'kmStart' wies sie jedes Tachofoto ab - und mit ihm das ganze Speichern.
  */
  const [fotos, setFotos] = useState<{ datei: File; feld: FotoFeld }[]>([]);

  /** Legt ein Foto zu einem Feld ab und ersetzt ein früheres desselben Feldes. */
  const fotoSetzen = (feld: FotoFeld, datei: File) =>
    setFotos((alt) => [...alt.filter((f) => f.feld !== feld), { datei, feld }]);
  const [fehler, setFehler] = useState<string[]>([]);
  const [laeuft, setLaeuft] = useState(false);

  const setzen = (schluessel: string, wert: string | boolean) =>
    setF((alt) => ({ ...alt, [schluessel]: wert }));

  const t = (schluessel: string) => String(f[schluessel] ?? '');
  const b = (schluessel: string) => Boolean(f[schluessel]);
  const n = (schluessel: string) => zuZahl(t(schluessel));

  /**
   * Text eines Feldes - solange niemand es angefasst hat, die Vorgabe.
   *
   * `undefined` heisst hier ausdrücklich "unberührt" und ist etwas anderes als
   * der leere Text: nur so lässt sich später unterscheiden, ob jemand den Wert
   * bewusst gesetzt oder das Feld nur stehen gelassen hat.
   */
  const mitVorgabe = (schluessel: string, vorgabe: string) =>
    f[schluessel] === undefined ? vorgabe : String(f[schluessel]);

  /**
   * Was dieser Ausflug ohne Zutun bekäme - die Regeln V1-V3 und P1/P2.
   *
   * Dafür wird der Ausflug probeweise in die Vorgangsliste gestellt und der
   * Rechenkern befragt. Nur so stimmt der Wert auch für ein zurückliegendes
   * Datum: ein Ausflug im Juni rechnet mit anderen Tankvorgängen als einer von
   * heute, und die Vorbelegung soll das zeigen.
   */
  const automatisch = useMemo(() => {
    if (art !== 'trip') return null;
    const probe: TripEntry = {
      id: vorhanden?.id ?? '__probe__',
      type: 'trip',
      datum: t('datum') || heuteIso(),
      // Ein neuer Ausflug landet am Ende seines Tages.
      seq: vorhanden?.seq ?? 9999,
      werUnterwegs: 'michi',
      kmGefahren: 1,
      anzHin: 1,
      gleicheAnzahl: true,
      zusatzkostenProKm: 0,
      anteilEinzelstrecke: 0.65,
      // Ohne diese beiden gäbe die Probe die eigenen Handwerte zurück und die
      // Vorbelegung wäre ein Zirkelschluss.
      verbrauchL100kmManuell: null,
      preisProLiterManuell: null,
      finalisiert: false,
    };
    const liste = [...entries.filter((e) => e.id !== probe.id), probe];
    const r = computeAll(liste, settings).trips.get(probe.id);
    return r ? { verbrauch: r.verbrauchL100km, preis: r.preisProLiter } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art, entries, settings, f['datum'], vorhanden?.id, vorhanden?.seq]);

  /*
    Besetzung aus der Liste (R-24). Sobald jemand die Anzahl von Hand ändert,
    hört die Vorbelegung auf - sonst überschriebe die Liste eine bewusste
    Korrektur, etwa wenn ein Name fehlt.
  */
  /*
    Ein laufender Ausflug (R-23) hat nur den Anfangsstand. Wird er BEARBEITET
    statt gestartet, greift trotzdem das volle Formular - und das verlangt eine
    Strecke. Deshalb gelten beim Bearbeiten eines laufenden Ausflugs dieselben
    Erleichterungen wie beim Starten: kein End-Zählerstand, keine Strecke,
    keine Rückfahrt-Besetzung.
  */
  const laeuftNoch =
    vorhanden?.type === 'trip' && vorhanden.status === 'laufend';
  const startet = modus === 'start' || (modus === 'voll' && laeuftNoch);
  const stoppt = modus === 'stopp';

  /*
    Namenlose Zeilen zählen nicht und werden nicht gespeichert.
    Wer versehentlich auf "+ MitfahrerIn" tippt und weitergehen will, soll
    nicht von einer Fehlermeldung aufgehalten werden - eine Zeile ohne Namen
    trägt keine Information.
  */
  const echteMitfahrer = mitfahrer.filter((m) => m.name.trim() !== '');
  /*
    Die Besetzung wird nur noch gerechnet, nicht mehr getippt.

    Vorher standen "Anzahl Hinfahrt" und "Anzahl Rückfahrt" als Felder da und
    liessen sich überstimmen (anz_manuell). Sobald sie von der Liste abwichen,
    stimmten die angezeigten Beiträge nicht mehr mit den Häkchen überein - und
    die Summe der Beiträge nicht mehr mit dem Ausflugspreis. Eine Quelle ist
    hier mehr wert als eine Ausweichmöglichkeit.
  */
  const abgeleitet = besetzungAusListe(
    (t('werUnterwegs') || 'michi') as TripEntry['werUnterwegs'],
    echteMitfahrer,
  );

  /*
    Ältere Ausflüge tragen noch von Hand gesetzte Besetzungszahlen, zu denen
    es keine Liste gibt. Beim Speichern gilt ab jetzt die Liste - das darf
    nicht stillschweigend passieren, denn es verschiebt die Beiträge.
  */
  const besetzungWeichtAb = (() => {
    const e = vorhanden?.type === 'trip' ? vorhanden : undefined;
    if (e == null) return null;
    const altRueck = e.gleicheAnzahl ? e.anzHin : (e.anzRueck ?? e.anzHin);
    if (e.anzHin === abgeleitet.anzHin && altRueck === abgeleitet.anzRueck) {
      return null;
    }
    return { alt: e.anzHin, altRueck };
  })();

  /*
    Ein Feld für alle drei Fälle: 'michi', 'otto' oder 'gast:<id>'. Getrennte
    Felder für "wer" und "welche MitfahrerIn" liessen sich widersprüchlich
    füllen.
  */
  const parkZahler = (() => {
    const gewaehlt = t('parkZahler');
    if (gewaehlt) return gewaehlt;
    const e = vorhanden?.type === 'trip' ? vorhanden : undefined;
    if (e?.parkgebuehrVon === 'mitfahrer' && e.parkgebuehrMitfahrerId) {
      return `gast:${e.parkgebuehrMitfahrerId}`;
    }
    return e?.parkgebuehrVon ?? 'michi';
  })();

  const vorgabeVerbrauch = automatisch ? zuText(automatisch.verbrauch, 2) : '';
  const vorgabePreis = automatisch ? zuText(automatisch.preis, 2) : '';

  /**
   * Liest eines der beiden Handfelder aus.
   *
   * null heisst "automatisch": Feld leer oder unverändert auf der Vorgabe.
   * Nur ein wirklich abweichender Wert wird am Ausflug festgeschrieben - so
   * wirkt ein später nachgetragener Tankvorgang weiterhin zurück.
   */
  function handwert(
    schluessel: string,
    vorgabe: string,
    titel: string,
    probleme: string[],
  ): number | null {
    const text = mitVorgabe(schluessel, vorgabe).trim();
    if (text === '' || text === vorgabe) return null;
    const wert = zuZahl(text);
    if (wert == null || wert <= 0) {
      probleme.push(`${titel} muss eine Zahl grösser als null sein.`);
      return null;
    }
    return wert;
  }

  /** Höchster KM-Stand aller Tankvorgänge vor dem eingegebenen Datum. */
  const kmStandDavor = useMemo(() => {
    const davor = entries
      .filter(
        (e): e is RefuelEntry =>
          e.type === 'refuel' &&
          !e.geloescht &&
          e.id !== vorhanden?.id &&
          e.datum <= t('datum'),
      )
      .sort((a, x) => (a.datum < x.datum ? -1 : 1));
    return davor.length > 0 ? davor[davor.length - 1]!.kmStand : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, f['datum'], vorhanden?.id]);

  function pruefeUndBaue(): Partial<Entry> | null {
    const probleme: string[] = [];
    const datum = t('datum');
    if (!datum) probleme.push('Das Datum fehlt.');

    const basis = {
      id: vorhanden?.id,
      type: art,
      datum,
      beschreibung: t('beschreibung').slice(0, 200) || undefined,
    };

    let entry: Partial<Entry> | null = null;

    if (art === 'trip') {
      const kmStart = n('kmStart');
      const kmEnde = n('kmEnde');
      const kmGefahren = n('kmGefahren');

      const werUnterwegs = t('werUnterwegs') as TripEntry['werUnterwegs'];

      if (!startet && !t('beschreibung')) probleme.push('Die Beschreibung fehlt.');

      if (startet) {
        // Beim Losfahren steht nur der Anfangsstand fest.
        if (kmStart == null) probleme.push('Der KM-Stand beim Start fehlt.');
      } else {
        const hatStrecke =
          (kmStart != null && kmEnde != null) || kmGefahren != null;
        if (!hatStrecke) {
          probleme.push(
            'Entweder KM-Stand Start und Ende oder die gefahrenen Kilometer eintragen.',
          );
        }
      }
      if (stoppt && kmEnde == null) {
        probleme.push('Der KM-Stand beim Stoppen fehlt.');
      }
      if (kmStart != null && kmEnde != null && kmEnde <= kmStart) {
        probleme.push('Der KM-Stand am Ende muss grösser sein als am Start.');
      }
      /*
        Niemand im Auto kann es nicht geben: Fährt keiner von euch mit, muss
        wenigstens eine MitfahrerIn in der Liste stehen, sonst gäbe es keinen,
        der den Ausflug bezahlt - und der Rechenkern teilte durch null.
      */
      if (abgeleitet.anzHin < 1) {
        probleme.push(
          'Wenn niemand von euch mitfährt, muss mindestens eine MitfahrerIn in der Liste stehen.',
        );
      }
      if (
        (werUnterwegs === 'beide' || werUnterwegs === 'keiner') &&
        !t('kassierer')
      ) {
        probleme.push(
          werUnterwegs === 'beide'
            ? 'Bei "Beide unterwegs" fehlt, wer die Beträge kassiert.'
            : 'Wenn niemand von euch mitfährt, muss feststehen, wer kassiert.',
        );
      }

      // Parkgebühr (R-22): Betrag und Zahler gehören zusammen.
      const park = n('parkgebuehr');
      const parkText = t('parkgebuehr').trim();
      if (parkText !== '' && (park == null || park <= 0)) {
        probleme.push('Die Parkgebühr muss ein Betrag grösser als null sein.');
      }
      if (
        parkText !== '' &&
        parkZahler.startsWith('gast:') &&
        !echteMitfahrer.some((m) => `gast:${m.id}` === parkZahler)
      ) {
        probleme.push(
          'Die Person, die geparkt hat, steht nicht mehr in der Liste.',
        );
      }
      const zusatz = n('zusatzkostenProKm');
      if (zusatz == null || zusatz < 0) {
        probleme.push('Die Zusatzkosten pro Kilometer fehlen.');
      }
      // Eingabe in Prozent, gespeichert wird der Anteil (R-21).
      const anteilProzent = n('anteilEinzelstrecke');
      if (anteilProzent == null || anteilProzent <= 0 || anteilProzent > 100) {
        probleme.push(
          'Der Anteil einer Einzelstrecke muss zwischen 1 und 100 Prozent liegen.',
        );
      }

      entry = {
        ...basis,
        type: 'trip',
        werUnterwegs,
        /*
          Auch bei "niemand von euns unterwegs" muss feststehen, wer kassiert
          - die Datenbank verlangt es (Migration 0006) und die Oberfläche
          fragt danach. Vorher fiel der Wert hier still weg und das Speichern
          endete in einem Serverfehler.
        */
        kassierer:
          werUnterwegs === 'beide' || werUnterwegs === 'keiner'
            ? (t('kassierer') as Person)
            : undefined,
        /*
          Ein leeres Feld muss als null hinausgehen, nicht als undefined:
          JSON.stringify lässt undefined weg, der Server behielte dann den
          alten Wert. Genau daran scheiterte das Löschen der gefahrenen
          Kilometer bei gesetztem Start und Ende.
        */
        kmStart: kmStart ?? null,
        kmEnde: kmEnde ?? null,
        kmGefahren: kmGefahren ?? null,
        anzHin: abgeleitet.anzHin,
        anzRueck: abgeleitet.anzRueck,
        gleicheAnzahl: abgeleitet.gleicheAnzahl,
        namenMitfahrer: t('namenMitfahrer') || undefined,
        zusatzkostenProKm: zusatz ?? 0,
        anteilEinzelstrecke: (anteilProzent ?? 65) / 100,
        /*
          Bearbeiten darf einen Ausflug nicht beenden. Vorher setzte jedes
          Speichern aus dem vollen Formular 'beendet' - ein laufender Ausflug
          wurde damit stillschweigend gestoppt.
        */
        status:
          modus === 'start'
            ? 'laufend'
            : modus === 'stopp'
              ? 'beendet'
              : laeuftNoch
                ? 'laufend'
                : 'beendet',
        mitfahrer: echteMitfahrer.map((m) => ({ ...m, name: m.name.trim() })),
        anzManuell: false,
        parkgebuehr: parkText !== '' && park != null && park > 0 ? park : null,
        parkgebuehrVon:
          parkText !== '' && park != null && park > 0
            ? parkZahler.startsWith('gast:')
              ? 'mitfahrer'
              : (parkZahler as TripEntry['parkgebuehrVon'])
            : null,
        parkgebuehrMitfahrerId:
          parkText !== '' && park != null && park > 0 && parkZahler.startsWith('gast:')
            ? parkZahler.slice('gast:'.length)
            : null,
        verbrauchL100kmManuell: handwert(
          'verbrauchL100kmManuell',
          vorgabeVerbrauch,
          'Der Verbrauch',
          probleme,
        ),
        preisProLiterManuell: handwert(
          'preisProLiterManuell',
          vorgabePreis,
          'Der Preis pro Liter',
          probleme,
        ),
      } as Partial<TripEntry>;
    }

    if (art === 'refuel') {
      const betrag = n('betrag');
      const liter = n('liter');
      const kmStand = n('kmStand');
      if (betrag == null || betrag <= 0) probleme.push('Der Betrag fehlt.');
      if (liter == null || liter <= 0) probleme.push('Die Liter fehlen.');
      if (kmStand == null) probleme.push('Der KM-Stand fehlt.');
      if (kmStand != null && kmStandDavor != null && kmStand <= kmStandDavor) {
        probleme.push(
          `Der KM-Stand muss über dem letzten Tankvorgang liegen (${kmStandDavor.toLocaleString('de-DE')} km).`,
        );
      }
      entry = {
        ...basis,
        type: 'refuel',
        bezahltVon: t('bezahltVon') as Person,
        betrag: betrag ?? 0,
        liter: liter ?? 0,
        kmStand: kmStand ?? 0,
        volltankung: b('volltankung'),
      };
    }

    if (art === 'repair' || art === 'fixed_cost') {
      const betrag = n('betrag');
      if (betrag == null || betrag <= 0) probleme.push('Der Betrag fehlt.');
      if (art === 'repair' && !t('beschreibung')) {
        probleme.push('Die Beschreibung fehlt.');
      }
      const repKm = n('kmStand');
      if (repKm != null && repKm < 0) {
        probleme.push('Der Kilometerstand kann nicht negativ sein.');
      }
      entry = {
        ...basis,
        type: art,
        // Auch hier: null löscht, undefined liesse den alten Stand stehen.
        ...(art === 'repair' ? { kmStand: repKm ?? null } : {}),
        bezahltVon: t('bezahltVon') as Person,
        betrag: betrag ?? 0,
        ...(art === 'fixed_cost'
          ? { kategorie: t('kategorie') || undefined }
          : {}),
      } as Partial<Entry>;
    }

    if (art === 'settlement') {
      const betrag = n('betrag');
      if (betrag == null || betrag <= 0) probleme.push('Der Betrag fehlt.');
      entry = {
        ...basis,
        type: 'settlement',
        richtung: t('richtung') as 'michi_an_otto' | 'otto_an_michi',
        betrag: betrag ?? 0,
      };
    }

    setFehler(probleme);
    return probleme.length === 0 ? entry : null;
  }

  async function speichern() {
    const entry = pruefeUndBaue();
    if (!entry) return;
    setLaeuft(true);
    try {
      await aufSpeichern(entry, fotos);
    } finally {
      setLaeuft(false);
    }
  }

  const literpreis =
    art === 'refuel' && n('betrag') && n('liter')
      ? (n('betrag')! / n('liter')!).toFixed(3).replace('.', ',')
      : null;

  const streckeAusStand =
    art === 'trip' && n('kmStart') != null && n('kmEnde') != null
      ? n('kmEnde')! - n('kmStart')!
      : null;

  const wahl = (
    schluessel: string,
    optionen: { wert: string; titel: string }[],
  ) => (
    <div className="wahl">
      {optionen.map((o) => (
        <button
          key={o.wert}
          type="button"
          aria-pressed={t(schluessel) === o.wert}
          onClick={() => setzen(schluessel, o.wert)}
        >
          {o.titel}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <div className="blatt__kopf">
        <h2 className="blatt__titel">
          {modus === 'start'
            ? 'Ausflug starten'
            : modus === 'stopp'
              ? 'Ausflug stoppen'
              : vorhanden
                ? `${ART_TITEL[art]} bearbeiten`
                : `${ART_TITEL[art]} anlegen`}
        </h2>
        <button className="schliessen" onClick={aufAbbrechen} aria-label="Schliessen">
          ×
        </button>
      </div>

      {laeuftNoch && modus === 'voll' && (
        <p className="hinweis hinweis--warnung" style={{ marginBottom: 12 }}>
          Dieser Ausflug läuft noch. Zählerstand beim Ankommen und Besetzung
          der Rückfahrt kommen erst beim Stoppen dazu.
        </p>
      )}

      <div className="feld">
        <label htmlFor="datum">Datum <Pflicht /></label>
        <input
          id="datum"
          type="date"
          value={t('datum')}
          onChange={(e) => setzen('datum', e.target.value)}
        />
      </div>

      {(art === 'trip' || art === 'repair' || art === 'fixed_cost') && (
        <div className="feld">
          <label htmlFor="beschreibung">
            Beschreibung {art === 'fixed_cost' ? null : <Pflicht />}
          </label>
          <textarea
            id="beschreibung"
            maxLength={200}
            value={t('beschreibung')}
            onChange={(e) => setzen('beschreibung', e.target.value)}
            placeholder={
              art === 'trip'
                ? 'Wohin ging es?'
                : art === 'repair'
                  ? 'Was wurde gemacht?'
                  : 'Versicherung, Steuer, Prüfung …'
            }
          />
        </div>
      )}

      {art === 'trip' && (
        <>
          {!stoppt && (
            <>
              <div className="feld">
                <span className="feld__titel">Wer ist unterwegs? <Pflicht /></span>
                {wahl('werUnterwegs', [
                  { wert: 'michi', titel: 'Michi' },
                  { wert: 'otto', titel: 'Otto' },
                  { wert: 'beide', titel: 'Beide' },
                  { wert: 'keiner', titel: 'Niemand' },
                ])}
                {t('werUnterwegs') === 'keiner' && (
                  <p className="hinweis">
                    Das Auto ist unterwegs, aber ohne euch. Ihr zahlt dann
                    keinen Sitzplatz mit – auch nicht am Parken.
                  </p>
                )}
              </div>

              {(t('werUnterwegs') === 'beide' ||
                t('werUnterwegs') === 'keiner') && (
                <div className="feld">
                  <span className="feld__titel">Wer kassiert? <Pflicht /></span>
                  {wahl('kassierer', [
                    { wert: 'michi', titel: 'Michi' },
                    { wert: 'otto', titel: 'Otto' },
                  ])}
                  <p className="hinweis">
                    Wer das Geld der MitfahrerInnen einsammelt, schuldet es der
                    gemeinsamen Kasse.
                  </p>
                </div>
              )}
            </>
          )}

          {/*
            Der Fotoknopf steht über dem Feld, das er füllt (R-27) - man
            fotografiert von oben nach unten durch das Formular.
          */}
          {startet ? (
            <div className="feld">
              <label htmlFor="kmStart">KM-Stand beim Losfahren <Pflicht /></label>
              <Zaehlerfoto
                feld="km_start"
                titel="Tacho fotografieren"
                aufWert={(kmStand) => setzen('kmStart', String(kmStand))}
                aufFoto={fotoSetzen}
              />
              <input
                id="kmStart"
                inputMode="decimal"
                value={t('kmStart')}
                onChange={(e) => setzen('kmStart', e.target.value)}
              />
              <p className="hinweis">Die untere, sechsstellige Zahl im Tacho.</p>
            </div>
          ) : stoppt ? (
            <div className="feld">
              <label htmlFor="kmEnde">KM-Stand beim Ankommen <Pflicht /></label>
              <Zaehlerfoto
                feld="km_ende"
                titel="Tacho fotografieren"
                aufWert={(kmStand) => setzen('kmEnde', String(kmStand))}
                aufFoto={fotoSetzen}
              />
              <input
                id="kmEnde"
                inputMode="decimal"
                value={t('kmEnde')}
                onChange={(e) => setzen('kmEnde', e.target.value)}
              />
              <p className="hinweis">
                Beim Losfahren standen{' '}
                {t('kmStart') ? `${t('kmStart')} km` : '– km'} auf dem Zähler.
              </p>
            </div>
          ) : (
            <>
              <div className="paar">
                <div className="feld">
                  <label htmlFor="kmStart">KM-Stand Start</label>
                  <input
                    id="kmStart"
                    inputMode="decimal"
                    value={t('kmStart')}
                    onChange={(e) => setzen('kmStart', e.target.value)}
                  />
                </div>
                <div className="feld">
                  <label htmlFor="kmEnde">KM-Stand Ende</label>
                  <input
                    id="kmEnde"
                    inputMode="decimal"
                    value={t('kmEnde')}
                    onChange={(e) => setzen('kmEnde', e.target.value)}
                  />
                </div>
              </div>
              {/*
                Auch beim Nachtragen: zwei Fotos, zwei Felder. Sie stehen
                unter dem Paar, weil die Felder hier nebeneinander liegen und
                ein Knopf über beiden nicht sagen würde, welches er füllt.
              */}
              <div className="paar">
                <Zaehlerfoto
                  feld="km_start"
                  titel="Foto Start"
                  aufWert={(kmStand) => setzen('kmStart', String(kmStand))}
                  aufFoto={fotoSetzen}
                />
                <Zaehlerfoto
                  feld="km_ende"
                  titel="Foto Ende"
                  aufWert={(kmStand) => setzen('kmEnde', String(kmStand))}
                  aufFoto={fotoSetzen}
                />
              </div>
            </>
          )}

          {!startet && !stoppt && (
          <div className="feld">
            <label htmlFor="kmGefahren">Gefahrene Kilometer <Pflicht /></label>
            <input
              id="kmGefahren"
              inputMode="decimal"
              value={t('kmGefahren')}
              onChange={(e) => setzen('kmGefahren', e.target.value)}
            />
            {streckeAusStand != null ? (
              <p className="hinweis">
                Aus Start und Ende ergeben sich{' '}
                {streckeAusStand.toLocaleString('de-DE')} km. Dieser Wert wird
                verwendet.
              </p>
            ) : (
              <p className="hinweis">
                Entweder hier eintragen oder oben Start und Ende ausfüllen.
              </p>
            )}
          </div>
          )}

          {/*
            "Anzahl Hinfahrt" und "Anzahl Rückfahrt" sind entfallen. Sie
            ergeben sich aus der Liste und ihren Häkchen; zwei Stellen für
            dieselbe Angabe gingen zwangsläufig irgendwann auseinander.
          */}

          <div className="feld" style={{ marginTop: 13 }}>
            <span className="feld__titel">MitfahrerInnen</span>
            {mitfahrer.length === 0 && (
              <p className="hinweis" style={{ marginTop: 0 }}>
                Noch niemand eingetragen. Namen sind freiwillig – sie helfen
                später beim Abhaken, wer schon bezahlt hat.
              </p>
            )}
            <ul className="gaesteliste">
              {mitfahrer.map((m, i) => (
                <li key={m.id} className="gast">
                  <input
                    className="gast__name-feld"
                    type="text"
                    maxLength={60}
                    value={m.name}
                    placeholder="Name"
                    aria-label={`Name der ${i + 1}. MitfahrerIn`}
                    onChange={(e) =>
                      setMitfahrer((alt) =>
                        alt.map((x, j) =>
                          j === i ? { ...x, name: e.target.value } : x,
                        ),
                      )
                    }
                  />
                  {/*
                    Zwei Haken statt eines Schalters: hin und zurück sind das,
                    was man tatsächlich weiss. Beide sind vorbelegt, denn die
                    ganze Fahrt ist der Normalfall.
                  */}
                  {(
                    [
                      ['hin', 'hin'],
                      ['rueck', 'zurück'],
                    ] as const
                  ).map(([feld, titel]) => (
                    <label key={feld} className="gast__strich">
                      <input
                        type="checkbox"
                        checked={m[feld]}
                        onChange={(e) =>
                          setMitfahrer((alt) =>
                            alt.map((x, j) =>
                              j === i ? { ...x, [feld]: e.target.checked } : x,
                            ),
                          )
                        }
                      />
                      {titel}
                    </label>
                  ))}
                  <button
                    type="button"
                    className="gast__weg"
                    aria-label={`${m.name || 'MitfahrerIn'} entfernen`}
                    onClick={() =>
                      setMitfahrer((alt) => alt.filter((_, j) => j !== i))
                    }
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="knopf knopf--leise"
              onClick={() =>
                setMitfahrer((alt) => [
                  ...alt,
                  {
                    id: `neu-${Date.now()}-${alt.length}`,
                    name: '',
                    hin: true,
                    rueck: true,
                    bezahlt: false,
                  },
                ])
              }
            >
              + MitfahrerIn
            </button>
            {besetzungWeichtAb && (
              <p className="hinweis hinweis--warnung">
                Bisher waren für diesen Ausflug {besetzungWeichtAb.alt} Personen
                auf der Hinfahrt vermerkt und {besetzungWeichtAb.altRueck} auf
                der Rückfahrt. Beim Speichern gilt die Liste:{' '}
                {abgeleitet.anzHin} hin, {abgeleitet.anzRueck} zurück. Fehlt
                jemand, trage ihn hier ein – sonst ändern sich die Beiträge.
              </p>
            )}
          </div>

          {/*
            Parkgebühr (R-22). Steht bewusst im normalen Ablauf und nicht im
            gedämpften Block: sie fällt beim Ausflug an, wird beim Stoppen
            eingetippt und gehört zu dem, was die MitfahrerInnen zahlen.
          */}
          <div className="feld">
            <label htmlFor="parkgebuehr">Parkgebühr in Euro</label>
            <input
              id="parkgebuehr"
              inputMode="decimal"
              value={t('parkgebuehr')}
              onChange={(e) => setzen('parkgebuehr', e.target.value)}
              placeholder="keine"
            />
          </div>

          {t('parkgebuehr').trim() !== '' && (
            <div className="feld">
              <span className="feld__titel">Wer hat das Parken bezahlt? <Pflicht /></span>
              {/*
                Namentlich statt nur "MitfahrerIn": die Liste steht ja
                ohnehin da, und beim Abrechnen will man wissen, wem man das
                Geld zurückgibt.
              */}
              <div className="wahl wahl--stapel">
                {[
                  { wert: 'michi', titel: NAME.michi },
                  { wert: 'otto', titel: NAME.otto },
                  ...echteMitfahrer.map((m) => ({
                    wert: `gast:${m.id}`,
                    titel: m.name,
                  })),
                ].map((o) => (
                  <button
                    key={o.wert}
                    type="button"
                    aria-pressed={parkZahler === o.wert}
                    onClick={() => setzen('parkZahler', o.wert)}
                  >
                    {o.titel}
                  </button>
                ))}
              </div>
              <p className="hinweis">
                Die Gebühr wird auf alle umgelegt wie die Fahrt selbst. Hat
                eine MitfahrerIn bezahlt, wird ihr Beitrag entsprechend
                kleiner – nach ihren eigenen Strecken, nicht pauschal.
              </p>
            </div>
          )}

          {/*
            Auch beim Stoppen: Verbrauch, Literpreis und Einzelstreckenanteil
            stehen dort schon zur Wahl, nur die Zusatzkosten fehlten - ohne
            Grund, denn sie gehen genauso in den Preis ein.
          */}
          <div className="feld">
            <label htmlFor="zusatz">Zusatzkosten pro Kilometer <Pflicht /></label>
            <input
              id="zusatz"
              inputMode="decimal"
              value={t('zusatzkostenProKm')}
              onChange={(e) => setzen('zusatzkostenProKm', e.target.value)}
            />
            <p className="hinweis">
              Wird beim Anlegen festgehalten und ändert sich später nicht mehr.
            </p>
          </div>
        </>
      )}

      {art === 'refuel' && (
        <>
          <div className="feld">
            <span className="feld__titel">Wer hat bezahlt? <Pflicht /></span>
            {wahl('bezahltVon', [
              { wert: 'michi', titel: 'Michi' },
              { wert: 'otto', titel: 'Otto' },
            ])}
          </div>

          <div className="paar">
            <div className="feld">
              <label htmlFor="betrag">Betrag in Euro <Pflicht /></label>
              <input
                id="betrag"
                inputMode="decimal"
                value={t('betrag')}
                onChange={(e) => setzen('betrag', e.target.value)}
              />
            </div>
            <div className="feld">
              <label htmlFor="liter">Liter <Pflicht /></label>
              <input
                id="liter"
                inputMode="decimal"
                value={t('liter')}
                onChange={(e) => setzen('liter', e.target.value)}
              />
            </div>
          </div>
          {literpreis && (
            <p className="hinweis" style={{ marginTop: -6 }}>
              Das sind {literpreis} € pro Liter.
            </p>
          )}

          <div className="feld" style={{ marginTop: 13 }}>
            <label htmlFor="kmStand">KM-Stand <Pflicht /></label>
            <input
              id="kmStand"
              inputMode="decimal"
              value={t('kmStand')}
              onChange={(e) => setzen('kmStand', e.target.value)}
            />
            {kmStandDavor != null && (
              <p className="hinweis">
                Letzter Tankvorgang: {kmStandDavor.toLocaleString('de-DE')} km.
              </p>
            )}
          </div>

          <label className="schalter">
            <input
              type="checkbox"
              checked={b('volltankung')}
              onChange={(e) => setzen('volltankung', e.target.checked)}
            />
            Vollgetankt
          </label>
          <p className="hinweis">
            Der Verbrauch lässt sich nur zwischen zwei vollen Tanks messen. Bei
            einer Teilbetankung hier abwählen.
          </p>
        </>
      )}

      {(art === 'repair' || art === 'fixed_cost') && (
        <>
          <div className="feld">
            <span className="feld__titel">Wer hat bezahlt? <Pflicht /></span>
            {wahl('bezahltVon', [
              { wert: 'michi', titel: 'Michi' },
              { wert: 'otto', titel: 'Otto' },
            ])}
          </div>
          <div className="feld">
            <label htmlFor="betrag">Betrag in Euro <Pflicht /></label>
            <input
              id="betrag"
              inputMode="decimal"
              value={t('betrag')}
              onChange={(e) => setzen('betrag', e.target.value)}
            />
          </div>
          {art === 'repair' && (
            <div className="feld">
              <label htmlFor="repKmStand">Kilometerstand</label>
              <input
                id="repKmStand"
                inputMode="decimal"
                value={t('kmStand')}
                onChange={(e) => setzen('kmStand', e.target.value)}
              />
            </div>
          )}
          {art === 'fixed_cost' && (
            <div className="feld">
              <label htmlFor="kategorie">Kategorie</label>
              <input
                id="kategorie"
                type="text"
                value={t('kategorie')}
                onChange={(e) => setzen('kategorie', e.target.value)}
                placeholder="Versicherung, Steuer, Prüfung"
              />
            </div>
          )}
        </>
      )}

      {art === 'settlement' && (
        <>
          <div className="feld">
            <span className="feld__titel">Wer zahlt an wen? <Pflicht /></span>
            {wahl('richtung', [
              { wert: 'michi_an_otto', titel: 'Michi an Otto' },
              { wert: 'otto_an_michi', titel: 'Otto an Michi' },
            ])}
          </div>
          <div className="feld">
            <label htmlFor="betrag">Betrag in Euro <Pflicht /></label>
            <input
              id="betrag"
              inputMode="decimal"
              value={t('betrag')}
              onChange={(e) => setzen('betrag', e.target.value)}
            />
          </div>
        </>
      )}

      {art !== 'settlement' && (
        <div className="feld">
          <label htmlFor="fotos">Fotos</label>
          <input
            id="fotos"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) =>
              setFotos((alt) => [
                ...alt.filter((f) => f.feld !== 'beleg'),
                ...Array.from(e.target.files ?? []).map((datei) => ({
                  datei,
                  feld: 'beleg' as FotoFeld,
                })),
              ])
            }
          />
          <p className="hinweis">
            {fotos.length > 0
              ? `${fotos.length} Foto${fotos.length === 1 ? '' : 's'} wird beim Speichern hochgeladen.`
              : 'Beleg oder Tacho abfotografieren, damit sich Zahlen später prüfen lassen.'}
          </p>
        </div>
      )}

      {/*
        Ganz unten und gedämpft: Verbrauch und Literpreis stehen normalerweise
        richtig da, sie kommen aus den Tankvorgängen. Angefasst werden sie
        selten - dann aber gezielt, etwa nach einer Autobahnfahrt.
      */}
      {/*
        Beim reinen Losfahren bleibt der Block weg - dort soll es schnell
        gehen. Beim BEARBEITEN eines laufenden Ausflugs gehört er dazu: die
        Werte hängen am Datum und an den Tankvorgängen, nicht an der Strecke,
        sind also auch vor dem Stoppen schon sinnvoll. Ohne das käme man an
        Verbrauch, Literpreis und Einzelstreckenanteil erst nach dem Stoppen
        heran.
      */}
      {art === 'trip' && automatisch && modus !== 'start' && (
        <section className="feinabgleich">
          <p className="etikett">Rechenwerte dieses Ausflugs</p>
          <div className="paar">
            <div className="feld">
              <label htmlFor="verbrauchManuell">Verbrauch l/100 km</label>
              <input
                id="verbrauchManuell"
                inputMode="decimal"
                value={mitVorgabe('verbrauchL100kmManuell', vorgabeVerbrauch)}
                onChange={(e) =>
                  setzen('verbrauchL100kmManuell', e.target.value)
                }
              />
            </div>
            <div className="feld">
              <label htmlFor="preisManuell">Preis pro Liter</label>
              <input
                id="preisManuell"
                inputMode="decimal"
                value={mitVorgabe('preisProLiterManuell', vorgabePreis)}
                onChange={(e) => setzen('preisProLiterManuell', e.target.value)}
              />
            </div>
          </div>
          <p className="hinweis" style={{ marginTop: 0 }}>
            Aus den Tankvorgängen vorbelegt. Stehen gelassen rechnet der Ausflug
            weiter automatisch mit – auch wenn du später noch tankst.
          </p>

          <div className="feld" style={{ marginTop: 14, marginBottom: 0 }}>
            <label htmlFor="anteilEinzel">
              Anteil einer Einzelstrecke in Prozent <Pflicht />
            </label>
            <input
              id="anteilEinzel"
              inputMode="numeric"
              value={t('anteilEinzelstrecke')}
              onChange={(e) => setzen('anteilEinzelstrecke', e.target.value)}
            />
            <p className="hinweis">
              Wer nur hin oder nur zurück mitfährt, zahlt diesen Anteil des
              vollen Beitrags. Wird beim Anlegen festgehalten.
            </p>
          </div>
        </section>
      )}

      {fehler.length > 0 && (
        <div className="tafel" style={{ borderColor: 'var(--warnung)' }}>
          {fehler.map((m) => (
            <p key={m} className="hinweis hinweis--warnung" style={{ margin: 0 }}>
              {m}
            </p>
          ))}
        </div>
      )}

      <div className="knopfreihe knopfreihe--fest">
        <button className="knopf knopf--leise" onClick={aufAbbrechen}>
          Abbrechen
        </button>
        <button className="knopf" onClick={speichern} disabled={laeuft}>
          {laeuft
            ? 'Speichert …'
            : modus === 'start'
              ? 'Losfahren'
              : modus === 'stopp'
                ? 'Angekommen'
                : 'Speichern'}
        </button>
      </div>

      <p className="hinweis" style={{ marginTop: 12 }}>
        Angemeldet als {NAME[ich]}.
      </p>
    </div>
  );
}

/**
 * Tachofoto aufnehmen und den Zählerstand daraus lesen lassen (R-27).
 *
 * Steht über dem Feld, das es füllt - man fotografiert von oben nach unten
 * durch das Formular. Der gelesene Wert landet im Feld und bleibt dort
 * änderbar; er ist ein Vorschlag, keine Messung. Was die Bilderkennung
 * gelesen hat, steht dabei, damit ein falscher Vorschlag erklärbar ist
 * statt rätselhaft.
 *
 * Das Foto wird in jedem Fall mit dem Vorgang gespeichert, auch wenn die
 * Erkennung nichts hergibt - dann ist es wenigstens der Beleg.
 */
function Zaehlerfoto({
  feld,
  titel,
  aufWert,
  aufFoto,
}: {
  feld: FotoFeld;
  titel: string;
  aufWert: (kmStand: number) => void;
  aufFoto: (feld: FotoFeld, datei: File) => void;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [stand, setStand] = useState<string | null>(null);

  async function gewaehlt(datei: File | undefined) {
    if (!datei) return;
    aufFoto(feld, datei);
    setLaeuft(true);
    setStand(null);
    try {
      const { kmStand, gelesen } = await api.liesZaehlerstand(datei);
      if (kmStand != null) {
        aufWert(kmStand);
        setStand(`Gelesen: ${kmStand.toLocaleString('de-DE')} km.`);
      } else {
        setStand(
          gelesen
            ? `Nicht sicher erkannt („${gelesen.replace(/\s+/g, ' ').slice(0, 40)}“) – bitte eintippen.`
            : 'Nichts erkannt – bitte eintippen.',
        );
      }
    } catch (e) {
      setStand(
        e instanceof Error
          ? `Das Lesen ging nicht: ${e.message}`
          : 'Das Lesen ging nicht.',
      );
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="tachofoto">
      <label className="tachofoto__knopf">
        <input
          type="file"
          accept="image/*"
          disabled={laeuft}
          onChange={(e) => void gewaehlt(e.target.files?.[0])}
        />
        {laeuft ? 'Wird gelesen …' : titel}
      </label>
      {stand && <p className="hinweis">{stand}</p>}
    </div>
  );
}
